/**
 * Shared (ov. current/max) label for cricket lobby cards: Squares, Trader, Games list.
 */

import {
  CRICKET_HUNDRED_FORMAT_CODE,
  cricketFormatScheduledOvers,
  normalizeCricketFormatCode,
} from "../../utils/cricketTestMatch";

/**
 * 1-based over from Decimal `curBall` (e.g. `4.3` → 5).
 * Null for pre-match placeholders (`-3`, empty, NaN) — those must not read as OVER NAN / live play.
 */
export function cricketLiveOverNumberFromCurBall(curBall) {
  if (curBall == null || curBall === "") return null;
  const ballOver = Number.parseFloat(String(curBall).trim());
  if (!Number.isFinite(ballOver) || ballOver < 0) return null;
  return Math.floor(ballOver) + 1;
}

/**
 * 1-based live over: prefer Decimal `curBall`, else innings overs bowled (`25.0` → 26).
 * Null when neither is a real in-play token.
 */
export function cricketCurrentOverOneBasedFromLiveState(curBall, inningsOversDecimal) {
  const fromBall = cricketLiveOverNumberFromCurBall(curBall);
  if (fromBall != null) return fromBall;
  if (inningsOversDecimal == null || inningsOversDecimal === "") return null;
  const dec = Number(inningsOversDecimal);
  if (!Number.isFinite(dec) || dec < 0) return null;
  // 0.0 without a live curBall is pretoss / unknown — do not paint "OVER 1".
  if (dec <= 1e-6) return null;
  return Math.floor(dec) + 1;
}

/** True when `curBall` is a real in-play ball token (not pre-toss `-3` / empty / NaN). */
export function cricketCurBallIndicatesLivePlay(curBall) {
  return cricketLiveOverNumberFromCurBall(curBall) != null;
}
/** Per-innings over limit from fixtures list (format id or label). Format 100 → 20 sets. */
export function maxOversFromFixtureFormat(format) {
  if (format == null || format === "") return null;
  if (typeof format === "number" && Number.isFinite(format)) {
    const scheduled = cricketFormatScheduledOvers(format);
    if (scheduled != null) return scheduled;
    return null;
  }
  const s = String(format).trim();
  const n = parseInt(s, 10);
  if (!Number.isNaN(n)) {
    const scheduled = cricketFormatScheduledOvers(n);
    if (scheduled != null) return scheduled;
  }
  const lower = s.toLowerCase();
  if (lower === "t20" || lower === "twenty20" || lower === "20") return 20;
  if (lower === "odi" || lower === "50" || lower === "one-day" || lower === "list a") return 50;
  if (lower === "t10" || lower === "10") return 10;
  if (/\bthe\s*hundred\b/.test(lower)) return 20;
  return null;
}

/** Infer T10/D10/T20/F50/Hundred from competition or fixture title when `format` is missing (align with backend `cricketMatchFormatOvers`). */
export function cricketFormatOversFromText(competition, matchName) {
  const blob = `${String(competition ?? "")} ${String(matchName ?? "")}`.toLowerCase();
  if (/\bthe\s*hundred\b/.test(blob)) return CRICKET_HUNDRED_FORMAT_CODE;
  // D10 / T10 before T20 so "Emirates D10" etc. are not skipped.
  if (/\bd10\b|\bt10s?\b|ten10|10.?over/.test(blob)) return 10;
  // Pro20 / SA20 nest "20" inside the brand (no `\bt20` token).
  if (/\bpro\s*20\b|\bsa\s*20\b|\bt20s?\b|twenty20|twenty-20/.test(blob)) return 20;
  if (/\bodi\b|\bf50\b|50.over|one[-\s]?day|list\s*a\b/.test(blob)) return 50;
  return null;
}

/** Resolve format code for Bullion lobby eligibility (20 / 50 / 100). */
export function bullionCricketFormatOversFromMatch(match) {
  if (!match || typeof match !== "object") return null;
  const code = normalizeCricketFormatCode(match.format ?? match.Format);
  if (code != null && code !== 999) return code;
  const fromMax = normalizeCricketFormatCode(match.max_overs ?? match.maxovers);
  if (fromMax != null && fromMax !== 999) return fromMax;
  return cricketFormatOversFromText(match.competition, match.name ?? match.match_name);
}

/** Parse a limited-overs max (1–50). Hundred sometimes sends `100` → 20 five-ball sets. */
function parseLimitedOversMax(value) {
  if (value == null || value === "") return null;
  const parsed = parseInt(String(value).trim(), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  if (parsed === 100) return 20;
  if (parsed > 50) return null;
  return parsed;
}

function maxOversFromArrayScoreboard(scoreboard) {
  if (!Array.isArray(scoreboard)) return null;
  const entry = scoreboard.find(
    (item) => item?.name === "Max Overs" || item?.name === "maxovers" || item?.name === "max.1",
  );
  return parseLimitedOversMax(entry?.value);
}

/** Delta object scoreboard (`maxovers`) as used by leagues SSE / useCricketStream. */
function maxOversFromObjectScoreboard(sb) {
  if (!sb || typeof sb !== "object" || Array.isArray(sb)) return null;
  return (
    parseLimitedOversMax(sb.maxovers) ??
    parseLimitedOversMax(sb.maxOvers) ??
    parseLimitedOversMax(sb.max_overs) ??
    parseLimitedOversMax(sb["Max Overs"])
  );
}

/** Innings overs decimal from delta object scoreboard (`currentinningsovers` + part). */
function decimalFromObjectScoreboard(sb) {
  if (!sb || typeof sb !== "object" || Array.isArray(sb)) return null;
  const fullRaw = sb.currentinningsovers ?? sb.currentInningsovers ?? sb.current_innings_overs;
  if (fullRaw == null || fullRaw === "") return null;
  const full = parseFloat(String(fullRaw).trim());
  if (!Number.isFinite(full) || full < 0) return null;
  const partRaw =
    sb.currentinningspartovers ?? sb.currentInningsPartOvers ?? sb.current_innings_part_overs;
  const part = partRaw == null || partRaw === "" ? 0 : parseFloat(String(partRaw).trim());
  const partSafe = Number.isFinite(part) && part >= 0 ? part : 0;
  return full + partSafe / 6;
}

/** Cricket notation `"12.3"` (12 overs, 3 balls) → true decimal `12.5`. */
function decimalFromCricketNotation(raw) {
  if (raw == null || raw === "") return null;
  const match = String(raw).trim().match(/^(\d+)(?:\.(\d))?$/);
  if (!match) return null;
  const full = parseInt(match[1], 10);
  if (!Number.isFinite(full) || full < 0) return null;
  if (match[2] == null) return full;
  const balls = parseInt(match[2], 10);
  if (!Number.isFinite(balls) || balls < 0 || balls > 5) return null;
  return full + balls / 6;
}

/** Current-innings overs from scorecard `innsN.overs` (cricket notation, not true decimal). */
function decimalFromScorecardInnings(streamData) {
  const card = streamData?.scorecard;
  if (!card || typeof card !== "object" || Array.isArray(card)) return null;
  const inn = Math.floor(Number(card.inns_now ?? card.innsNow));
  if (!Number.isFinite(inn) || inn < 1) return null;
  const inns = card[`inns${inn}`];
  if (!inns || typeof inns !== "object" || Array.isArray(inns)) return null;
  return decimalFromCricketNotation(inns.overs);
}

/**
 * Overs bowled in the current innings.
 * Legacy array scoreboard, then the delta object leagues SSE stores on `scoreboard`,
 * then scorecard `innsN.overs`.
 */
export function cricketInningsOversDecimalFromStream(streamData) {
  if (!streamData || typeof streamData !== "object") return null;
  const eventObj = streamData.event?.event ?? streamData.event;
  const eventBoard = eventObj?.scoreboard;
  return (
    cricketInningsOversDecimalFromScoreboard(eventBoard) ??
    decimalFromObjectScoreboard(streamData.scoreboard) ??
    decimalFromObjectScoreboard(eventBoard) ??
    decimalFromScorecardInnings(streamData)
  );
}

/**
 * `(ov. 12.3/50)` when the innings cap is a limited-overs max.
 * `(ov. 12.3)` when the over is known but there is no cap (Test, or maxovers still blank).
 * Null only when neither the over nor a cap is known.
 */
function formatCricketStickyOvLabel(decimalOvers, maxOvers) {
  const capped =
    maxOvers != null && Number.isFinite(Number(maxOvers))
      ? parseLimitedOversMax(maxOvers)
      : null;
  const num =
    decimalOvers != null && Number.isFinite(Number(decimalOvers)) && Number(decimalOvers) >= 0
      ? formatCricketOversBowledDisplay(Number(decimalOvers))
      : null;
  if (num == null && capped == null) return null;
  if (capped != null) return `(ov. ${num ?? "—"}/${capped})`;
  return `(ov. ${num})`;
}

/**
 * Live feed innings over cap (e.g. DLS / T20 limit).
 * Prefers legacy array "Max Overs", then delta object `maxovers`, then event.max_overs.
 */
export function maxOversFromStreamEvent(streamData) {
  if (!streamData || typeof streamData !== "object") return null;
  const eventObj = streamData.event?.event ?? streamData.event;
  return (
    maxOversFromArrayScoreboard(eventObj?.scoreboard) ??
    maxOversFromObjectScoreboard(streamData.scoreboard) ??
    maxOversFromObjectScoreboard(eventObj?.scoreboard) ??
    parseLimitedOversMax(eventObj?.max_overs) ??
    parseLimitedOversMax(streamData.formatOvers) ??
    parseLimitedOversMax(streamData.scheduledOvers) ??
    null
  );
}

/** Same 1-based “current over” as match play: floor(full + part/6) + 1. */
export function cricketCurrentOver1BasedFromScoreboard(scoreboard) {
  if (!Array.isArray(scoreboard)) return null;
  const fullEntry = scoreboard.find((item) => item?.name === "Current Innings Overs");
  const partEntry = scoreboard.find((item) => item?.name === "Current Innings Part Overs");
  const fullRaw = fullEntry?.value;
  if (fullRaw == null || fullRaw === "") return null;
  const full = parseFloat(String(fullRaw).trim());
  if (!Number.isFinite(full) || full < 0) return null;
  const partRaw = partEntry?.value;
  const part = partRaw == null || partRaw === "" ? 0 : parseFloat(String(partRaw).trim());
  const partSafe = Number.isFinite(part) && part >= 0 ? part : 0;
  const decimal = full + partSafe / 6;
  return Math.floor(decimal) + 1;
}

/** Decimal overs bowled (full + part/6) from scoreboard, same basis as Trader scorecard / CurPos. */
export function cricketInningsOversDecimalFromScoreboard(scoreboard) {
  if (!Array.isArray(scoreboard)) return null;
  const fullEntry = scoreboard.find((item) => item?.name === "Current Innings Overs");
  const partEntry = scoreboard.find((item) => item?.name === "Current Innings Part Overs");
  const fullRaw = fullEntry?.value;
  if (fullRaw == null || fullRaw === "") return null;
  const full = parseFloat(String(fullRaw).trim());
  if (!Number.isFinite(full) || full < 0) return null;
  const partRaw = partEntry?.value;
  const part = partRaw == null || partRaw === "" ? 0 : parseFloat(String(partRaw).trim());
  const partSafe = Number.isFinite(part) && part >= 0 ? part : 0;
  return full + partSafe / 6;
}

/** Cricket display string e.g. "1.2" (1 over, 2 balls), aligned with Trader scorecard total overs line. */
export function formatCricketOversBowledDisplay(decimalOvers) {
  if (decimalOvers == null || typeof decimalOvers !== "number" || Number.isNaN(decimalOvers)) return null;
  const full = Math.floor(decimalOvers);
  let balls = Math.round((decimalOvers - full) * 6);
  if (balls >= 6) balls = 0;
  return balls === 0 && full > 0 ? `${full}.0` : `${full}.${balls}`;
}

/**
 * Match-play footer / tabs: `"5.3"` = **1-based** current over · legal balls in this over (six-ball basis).
 * Derived from the same innings decimal as sticky `(ov. 4.3/20)` (`full + part/6`).
 */
export function formatCricketCurrentOverProgressLabel(decimalOvers) {
  if (decimalOvers == null || typeof decimalOvers !== "number" || Number.isNaN(decimalOvers) || decimalOvers < 0) return null;
  const full = Math.floor(decimalOvers);
  let balls = Math.round((decimalOvers - full) * 6);
  if (balls >= 6) balls = 0;
  const oneBasedOver = full + 1;
  return `${oneBasedOver}.${balls}`;
}

/**
 * Per-innings over cap from feed `mOvers` (innings1–4 max) when present; else fixture format (T20/ODI).
 * @param {number} inningsIndex1Based 1–4
 * @param {unknown} mOvers - e.g. from socket: max per innings or [max.1, max.2]
 * @param {unknown} fixtureFormat
 * @returns {number|null}
 */
export function maxOversForInningsIndex(inningsIndex1Based, mOvers, fixtureFormat) {
  const inn = Math.floor(Number(inningsIndex1Based));
  if (!Number.isFinite(inn) || inn < 1 || inn > 4) return maxOversFromFixtureFormat(fixtureFormat);
  const idx = inn - 1;
  if (Array.isArray(mOvers) && idx >= 0 && idx < mOvers.length) {
    const v = mOvers[idx];
    if (v != null && v !== "") {
      const parsed = parseInt(String(v).trim(), 10);
      if (Number.isFinite(parsed) && parsed > 0 && parsed <= 50) return parsed;
    }
  }
  return maxOversFromFixtureFormat(fixtureFormat);
}

/**
 * Lobby-style label for match-play sticky bar when live: `(ov. 2.1/20)`.
 * Uses current-innings decimal overs from socket + max for `innsNow`.
 */
/** True when the sticky label would show `0.0` overs bowled (e.g. `(ov. 0.0/20)`). */
export function isSquaresAtInningsOversZero(currentInningsOversDecimal) {
  const dec =
    currentInningsOversDecimal != null && Number.isFinite(Number(currentInningsOversDecimal))
      ? Number(currentInningsOversDecimal)
      : NaN;
  return Number.isFinite(dec) && dec <= 1e-6;
}

/**
 * Innings switch / chase not started: sticky `(ov. 0.0/20)`, `(ov. 0.0/50)`, etc.
 * Same signal the header uses — not end-of-innings `(ov. 20.0/20)`.
 */
export function isSquaresInningsSwitchOvState(
  currentInningsOversDecimal,
  inningsIndex1Based,
  mOvers,
  fixtureFormat
) {
  if (!isSquaresAtInningsOversZero(currentInningsOversDecimal)) return false;
  const max = maxOversForInningsIndex(inningsIndex1Based, mOvers, fixtureFormat);
  return max != null && max >= 1;
}

/**
 * True when live overs bowled have reached the innings cap — same basis as sticky `(ov. 20.0/20)` / `(ov. 50.0/50)`.
 */
export function isSquaresAtInningsOversCap(currentInningsOversDecimal, inningsIndex1Based, mOvers, fixtureFormat) {
  const max = maxOversForInningsIndex(inningsIndex1Based, mOvers, fixtureFormat);
  if (max == null) return false;
  const dec =
    currentInningsOversDecimal != null && Number.isFinite(Number(currentInningsOversDecimal))
      ? Number(currentInningsOversDecimal)
      : NaN;
  if (!Number.isFinite(dec)) return false;
  return dec >= max - 1e-6;
}

/** Highest `ov` on scoregrid for this innings (integer over index, e.g. 20 at end of a T20). */
export function scoregridMaxOverNumber(scoreGridData, inningsIndex1Based) {
  const inn = Math.floor(Number(inningsIndex1Based));
  if (!Number.isFinite(inn) || inn < 1) return null;
  const arr = scoreGridData?.overs?.[`inns${inn}`];
  if (!Array.isArray(arr) || arr.length === 0) return null;
  let hi = 0;
  for (const row of arr) {
    const ov = Number(row?.ov);
    if (Number.isFinite(ov) && ov > hi) hi = ov;
  }
  return hi > 0 ? hi : null;
}

export function isSquaresScoregridAtInningsOverCap(scoreGridData, inningsIndex1Based, mOvers, fixtureFormat) {
  const max = maxOversForInningsIndex(inningsIndex1Based, mOvers, fixtureFormat);
  const gridMax = scoregridMaxOverNumber(scoreGridData, inningsIndex1Based);
  if (max == null || gridMax == null) return false;
  return gridMax >= max;
}

/**
 * Parse fixture `format` / `max_overs` from stream or list (numeric 50, string "T20", etc.).
 * Returns scheduled overs for display (format 100 → 20 sets). Test → 999.
 * @param {unknown} raw
 * @returns {number|null} scheduled overs, 999 for Test, or null
 */
export function parseCricketFixtureFormatOvers(raw) {
  if (raw == null || raw === "") return null;
  const normalized = normalizeCricketFormatCode(raw);
  if (normalized === 999) return 999;
  if (normalized != null) {
    return cricketFormatScheduledOvers(normalized) ?? normalized;
  }
  return maxOversFromFixtureFormat(raw);
}

/**
 * Scheduled overs cap for the live innings — same resolution as the sticky `(ov. x/20)` label.
 * Treats `0` / blank live `mOvers` slots as missing so second-innings RRR can fall back to format.
 */
export function resolveCricketMatchPlayMaxOvers(
  innsNow,
  mOvers,
  fixtureFormat,
  liveMaxOversFromScoreboard = null
) {
  const inn = Math.floor(Number(innsNow));
  const indexedMax =
    Number.isFinite(inn) && inn >= 1 && inn <= 4
      ? maxOversForInningsIndex(inn, mOvers, fixtureFormat)
      : maxOversFromFixtureFormat(fixtureFormat);
  return parseLimitedOversMax(liveMaxOversFromScoreboard) ?? indexedMax;
}

export function getCricketMatchPlayStickyOvLabel(
  currentInningsOversDecimal,
  innsNow,
  mOvers,
  fixtureFormat,
  liveMaxOversFromScoreboard = null
) {
  const max = resolveCricketMatchPlayMaxOvers(
    innsNow,
    mOvers,
    fixtureFormat,
    liveMaxOversFromScoreboard
  );
  let dec = null;
  if (currentInningsOversDecimal != null && !Number.isNaN(Number(currentInningsOversDecimal))) {
    dec = Number(currentInningsOversDecimal);
  }
  return formatCricketStickyOvLabel(dec, max);
}

/**
 * Full display label including parentheses, e.g. "(ov. 12.3/50)" or "(ov. 89.4)".
 * Uses overs bowled (balls notation), not 1-based “current over” index.
 * The over number is kept when the innings has no limited-overs cap (Test, or maxovers blank).
 * @param {object|undefined} streamData - merged SSE payload (event.scoreboard or delta scoreboard)
 * @param {unknown} fixtureFormat - from fixtures list when stream max not yet available
 */
export function getCricketMatchCardOvLabel(streamData, fixtureFormat) {
  const max = maxOversFromStreamEvent(streamData) ?? maxOversFromFixtureFormat(fixtureFormat);
  const dec = cricketInningsOversDecimalFromStream(streamData);
  return formatCricketStickyOvLabel(dec, max);
}
